import React, {useMemo} from "react";
import {CostCenter} from "../../../../models/CostCenter";
import "./treeCostCenter.scss";
import TreeDrawing from "./TreeDrawing";
import {useTranslation} from "react-i18next";
import Section from "../../../../models/Section";
import {DrawingType} from "../../../../models/enums";
import {UseTreeExpandData} from "../../hooks/useTreeExpandData";
import {TreeRow} from "./TreeRow";
import {useProjectDetailContext} from "../../providers/ProjectDetailProvider";
import TreeCostCenterHeader from "./TreeCostCenterHeader";
import {EmptyTreeRowLabel} from "./EmptyTreeRowLabel";
import {CostCenterDrawing} from "../../../../models/CostCenterDrawing";

/**
 * Comparator for sorting sibling drawings.
 * Sorts by name, then by drawing type.
 */
function siblingDrawingComparator(a: CostCenterDrawing, b: CostCenterDrawing) {
    const nameComparison = a.name.localeCompare(b.name);

    if (nameComparison !== 0) {
        return nameComparison;
    }

    const drawingTypeOrder = {
        [DrawingType.TAKE_OFF]: 1,
        [DrawingType.AS_BUILT]: 2,
        [DrawingType.ASSET_PLAN]: 3,
    };

    const typeAOrder = drawingTypeOrder[a.drawingType];
    const typeBOrder = drawingTypeOrder[b.drawingType];

    return typeAOrder - typeBOrder;
}

interface ITreeCostCenterProps {
	costCenter: CostCenter,
	section: Section,
	treeExpandData: UseTreeExpandData
	openCreateBlankDrawingModal: (costCenterId?: string, sectionId?: string) => void
}

const TreeCostCenter: React.FC<ITreeCostCenterProps> = ({costCenter, section, treeExpandData, openCreateBlankDrawingModal}) => {

	const {t} = useTranslation();
	const {treeData} = useProjectDetailContext()
	const {isCostCenterSelected, selectCostCenter} = treeData.selectionData
	const open = treeExpandData.isExpanded(costCenter.id)

	const _changeSelection = () => {
		selectCostCenter(section.id, costCenter.id);
	};
	const toggleOpen = () => {
		treeExpandData.toggleCostCenter(costCenter.id)
	};

	const onCreateDrawingClick = () => {
		openCreateBlankDrawingModal(costCenter.id, section.id)
	}

    const sortedDrawings = useMemo(() => {
        const originalDrawings = costCenter.drawings;
        const drawingIds = new Set(originalDrawings.map(d => d.id));

        const drawings = originalDrawings.map(drawing => {
            if (drawing.parentId && !drawingIds.has(drawing.parentId)) {
                // The parentId is invalid, so create a new drawing object with parentId as undefined.
                return new CostCenterDrawing(
                    drawing.id,
                    drawing.name,
                    drawing.drawingType,
                    drawing.locked,
                    drawing.versions,
                    drawing.modifiedAt,
                    drawing.modifiedBy,
                    undefined
                );
            }
            return drawing;
        });

        const childrenByParentId = new Map<string, CostCenterDrawing[]>();
        const rootDrawings: CostCenterDrawing[] = [];

        // Partition drawings into root drawings and children of other drawings
        for (const drawing of drawings) {
            if (drawing.parentId) {
                const parentChildren = childrenByParentId.get(drawing.parentId) ?? [];
                parentChildren.push(drawing);
                childrenByParentId.set(drawing.parentId, parentChildren);
            } else {
                rootDrawings.push(drawing);
            }
        }

        // Sort children within each group by name and type
        for (const children of Array.from(childrenByParentId.values())) {
            children.sort(siblingDrawingComparator);
        }

        // Sort the root drawings by name and type
        rootDrawings.sort(siblingDrawingComparator);

        const result: CostCenterDrawing[] = [];
        function flatten(drawing: CostCenterDrawing) {
            result.push(drawing);
            const children = childrenByParentId.get(drawing.id);
            if (children) {
                for (const child of children) {
                    flatten(child);
                }
            }
        }

        for (const root of rootDrawings) {
            flatten(root);
        }

        return result;
    }, [costCenter.drawings]);

	return (
		<TreeRow
			header={(
				<TreeCostCenterHeader
					isDeleted={costCenter.deletedExternal && !section.deletedExternal}
					costCenter={costCenter}
					isChecked={isCostCenterSelected(section.id, costCenter.id)}
					onChange={_changeSelection}/>
			)}
			toggleExpand={toggleOpen}
			isOpen={open}>
			{sortedDrawings.length === 0 ?
				<EmptyTreeRowLabel
					onClick={onCreateDrawingClick}
					textBeforeButton={t(`projects.details.noDrawingsInTreeMessage`)}
					textAfterButton={t(`projects.details.blankTakeoff`)}/>
				: sortedDrawings.map(dr =>
					<TreeDrawing
						key={dr.id}
						drawing={dr}
						section={section}
						costCenter={costCenter}
					/>
				)
			}
		</TreeRow>
	);
};

export default TreeCostCenter
